import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Empty, Row, Table } from "antd";
import { Clock } from "lucide-react";
import dayjs from "dayjs";
import { useOrderBroadcast } from "../../../hooks/useOrderBroadcast";
import CashAndCarryLogo from "../../../assets/img/Magna-Cash-and-Carry.png";
import { formatCurrency } from "../../../utils";
import { ShoppingOutlined } from "@ant-design/icons";
import AdvertisementCarousel from "../../../components/ui/AdvertisementCarousel";

const OrderSummery = () => {
    const { orderItems = [], orderDate, totals = {}, customer } = useOrderBroadcast();

    const [currentTime, setCurrentTime] = useState(() => dayjs().format("ddd, DD MMM YYYY • HH:mm:ss"));
    const tableContainerRef = useRef(null);

    // Live clock
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(dayjs().format("ddd, DD MMM YYYY • HH:mm:ss"));
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Automatic Fullscreen: triggers on load and on user interaction
    useEffect(() => {
        const enterFullscreen = () => {
            if (!document.fullscreenElement) {
                const docEl = document.documentElement;
                if (docEl.requestFullscreen) {
                    docEl.requestFullscreen().catch(() => { });
                } else if (docEl.webkitRequestFullscreen) {
                    docEl.webkitRequestFullscreen();
                } else if (docEl.msRequestFullscreen) {
                    docEl.msRequestFullscreen();
                }
            }
        };

        // Attempt immediately on mount
        enterFullscreen();

        // Browser security policy may require a user gesture; trigger on interaction
        const handleInteraction = () => {
            enterFullscreen();
        };

        window.addEventListener("click", handleInteraction);
        window.addEventListener("keydown", handleInteraction);
        window.addEventListener("touchstart", handleInteraction);

        return () => {
            window.removeEventListener("click", handleInteraction);
            window.removeEventListener("keydown", handleInteraction);
            window.removeEventListener("touchstart", handleInteraction);
        };
    }, []);

    // Auto-scroll table body to newest scanned item
    useEffect(() => {
        if (orderItems.length > 0 && tableContainerRef.current) {
            const scrollableBody =
                tableContainerRef.current.querySelector?.('.ant-table-body') ||
                tableContainerRef.current;
            if (scrollableBody) {
                scrollableBody.scrollTop = scrollableBody.scrollHeight;
            }
        }
    }, [orderItems.length]);

    // Computed total items & total pieces
    const totalQuantity = useMemo(() => {
        return orderItems.reduce((acc, it) => acc + Number(it.order_item_quantity ?? it.quantity ?? 1), 0);
    }, [orderItems]);

    // Customer name & initials
    const customerName = customer?.name || customer?.title || "";
    const customerInitials = useMemo(() => {
        if (!customerName) return "WI";
        const parts = customerName.trim().split(/\s+/);
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }, [customerName]);

    // Responsive single table columns (numeric columns fixed, name fills available space)
    const columns = [
        {
            title: "#",
            key: "row_idx",
            width: 46,
            align: "center",
            render: (_, __, index) => <span className="pos-idx">{index + 1}</span>,
        },
        {
            title: "Name",
            dataIndex: "item_name",
            key: "item_name",
            render: (text, record) => (
                <div className="pos-item-col">
                    <span className="pos-item-name">{text || record.product_name || "Item"}</span>
                    {record.item_code && <span className="pos-item-sku">SKU: {record.item_code}</span>}
                </div>
            ),
        },
        {
            title: "Qty",
            dataIndex: "order_item_quantity",
            key: "order_item_quantity",
            width: 55,
            align: "center",
            render: (qty) => <span className="pos-qty">{qty}</span>,
        },
        {
            title: "UOM",
            dataIndex: "uom",
            key: "uom",
            width: 65,
            align: "center",
            render: (text) => <span className="pos-uom" style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>{text || 'Case'}</span>,
        },
        {
            title: "Price",
            dataIndex: "order_item_unit_price",
            key: "order_item_unit_price",
            width: 95,
            align: "right",
            render: (price) => <span className="pos-num">{formatCurrency(price)}</span>,
        },
        {
            title: "VAT%",
            dataIndex: "vat_percentage",
            key: "vat_percentage",
            width: 72,
            align: "center",
            render: (vat) => {
                const vatNum = parseFloat(vat) || 0;
                return (
                    <span className={vatNum > 0 ? "pos-vat-active" : "pos-vat-zero"}>
                        {vatNum > 0 ? `${vatNum.toFixed(0)}%` : "0%"}
                    </span>
                );
            },
        },
        {
            title: "VAT(£)",
            dataIndex: "order_item_vat",
            key: "order_item_vat",
            width: 88,
            align: "right",
            render: (vat) => <span className="pos-num pos-vat-val">{formatCurrency(vat)}</span>,
        },
        {
            title: "Total (£)",
            dataIndex: "order_item_total",
            key: "order_item_total",
            width: 105,
            align: "right",
            render: (total) => <span className="pos-total-val">{formatCurrency(total)}</span>,
        },
    ];

    const grandTotal = Number(totals?.grandTotal || 0);
    const subtotal = Number(totals?.subtotal || 0);
    const vatTotal = Number(totals?.vat || 0);
    const cashPaid = Number(totals?.cash || 0);
    const cardPaid = Number(totals?.card || 0);
    const totalPaid = cashPaid + cardPaid;
    const remainingAmount = Number(
        totals?.remaining_amount !== undefined && totals?.remaining_amount !== null
            ? totals.remaining_amount
            : Math.max(0, grandTotal - totalPaid)
    );
    const isPaidInFull = grandTotal > 0 && totalPaid >= grandTotal;

    if (orderItems.length === 0) {
        return (
            <div style={{ width: '100vw', height: '100vh', margin: 0, padding: 0, backgroundColor: '#000', overflow: 'hidden' }}>
                <AdvertisementCarousel />
            </div>
        );
    }

    return (
        <div className="cfd-root order-summery">
            {/* Top Brand & Real-Time Terminal Header */}
            <header className="cfd-header">
                <div className="cfd-header-brand">
                    <img
                        alt="Magna Cash & Carry"
                        src={CashAndCarryLogo}
                        className="cfd-logo-img"
                    />
                    <div className="cfd-brand-divider" />
                    <span className="cfd-portal-subtitle">Customer Display</span>
                </div>

                {/* Real-time Clock */}
                <div className="cfd-header-center">
                    <div className="cfd-live-clock">
                        <Clock size={15} className="cfd-clock-icon" />
                        <span>{currentTime}</span>
                    </div>
                </div>
            </header>

            {/* Main Content Area */}
            <main className="cfd-body">
                <Row className="cfd-row">
                    {/* LEFT COLUMN: Customer Info */}
                    <Col xs={{ span: 24, order: 1 }} md={{ span: 12, order: 1 }} lg={{ span: 12, order: 1 }} xl={{ span: 5, order: 1 }} className="cfd-col-left">
                        <Card
                            className="pos-panel pos-customer-panel"
                            title="Customer Info"
                            variant="borderless"
                        >
                            <div className="pos-customer-content">
                                <div className="pos-customer-profile-box">
                                    <div className="pos-customer-avatar">{customerInitials}</div>
                                    <div className="pos-customer-meta">
                                        <div className="pos-customer-name">
                                            {customerName || "Walk-In Customer"}
                                        </div>
                                        <div className="pos-customer-badge">
                                            {customerName ? "Trade Account" : "Retail Checkout"}
                                        </div>
                                    </div>
                                </div>

                                <div className="pos-kv-list">
                                    <div className="pos-kv-item">
                                        <span className="pos-kv-label">Email:</span>
                                        <span className="pos-kv-val" title={customer?.email || ""}>
                                            {customer?.email || "—"}
                                        </span>
                                    </div>

                                    <div className="pos-kv-item">
                                        <span className="pos-kv-label">Phone:</span>
                                        <span className="pos-kv-val">
                                            {customer?.phone || "—"}
                                        </span>
                                    </div>

                                    <div className="pos-kv-item">
                                        <span className="pos-kv-label">Date:</span>
                                        <span className="pos-kv-val">
                                            {orderDate || dayjs().format("DD-MM-YYYY")}
                                        </span>
                                    </div>
                                </div>

                                <div className="pos-customer-summary-meta">
                                    <div className="pos-meta-pill">
                                        <span>Total Items:</span>
                                        <strong>{orderItems.length}</strong>
                                    </div>
                                    <div className="pos-meta-pill">
                                        <span>Total Quantities:</span>
                                        <strong>{totalQuantity}</strong>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Col>

                    {/* CENTER COLUMN: Order Items Table (Sticky Header aligned with Card Headers, no redundant 'Items in Order' header) */}
                    <Col xs={{ span: 24, order: 3 }} md={{ span: 24, order: 3 }} lg={{ span: 24, order: 3 }} xl={{ span: 14, order: 2 }} className="cfd-col-center">
                        <Card
                            className="pos-panel pos-items-panel"
                            variant="borderless"
                        >
                            <div className="pos-table-scroll-container" ref={tableContainerRef}>
                                {!!orderItems?.length ? (
                                    <Table
                                        dataSource={orderItems}
                                        columns={columns}
                                        pagination={false}
                                        size="middle"
                                        rowKey={(rec, idx) => rec.item_id || rec.id || idx}
                                        className="pos-table pos-table-single"
                                        sticky
                                        locale={{
                                            emptyText: (
                                                <div className="pos-empty-wrap">
                                                    <Empty
                                                        description={
                                                            <div>
                                                                <div className="pos-empty-heading">No items added yet</div>
                                                                <div className="pos-empty-sub">Items will appear here as they are added</div>
                                                            </div>
                                                        }
                                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                    />
                                                </div>
                                            ),
                                        }}
                                    />
                                ) : (
                                    <div className="pos-empty-wrap">
                                        <Empty
                                            description={
                                                <div>
                                                    <div className="pos-empty-heading">No items added yet</div>
                                                    <div className="pos-empty-sub">Items will appear here as they are added</div>
                                                </div>
                                            }
                                            image={<ShoppingOutlined style={{ fontSize: 100, color: '#d5dbe0' }} />}
                                        />
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Col>

                    {/* RIGHT COLUMN: Summary & Payment */}
                    <Col xs={{ span: 24, order: 2 }} md={{ span: 12, order: 2 }} lg={{ span: 12, order: 2 }} xl={{ span: 5, order: 3 }} className="cfd-col-right">
                        <Card
                            className="pos-panel pos-summary-panel"
                            title={
                                <div className="pos-summary-title-row">
                                    <span>Order Summary</span>
                                </div>
                            }
                            variant="borderless"
                        >
                            <div className="pos-summary-content">
                                {/* Subtotal & VAT */}
                                <div className="pos-summary-breakdown">
                                    <div className="pos-sum-row">
                                        <span className="pos-sum-label">Subtotal (Exc. VAT)</span>
                                        <strong className="pos-sum-val">{formatCurrency(subtotal)}</strong>
                                    </div>

                                    <div className="pos-sum-row">
                                        <span className="pos-sum-label">Total VAT</span>
                                        <strong className="pos-sum-val">{formatCurrency(vatTotal)}</strong>
                                    </div>
                                </div>

                                {/* GRAND TOTAL HERO CARD - SOLID NAVY */}
                                <div className="pos-grand-total-card">
                                    <div className="pos-gt-left">
                                        <span className="pos-gt-label">GRAND TOTAL</span>
                                        <span className="pos-gt-sub">INCL. VAT</span>
                                    </div>
                                    <span className="pos-gt-amount">{formatCurrency(grandTotal)}</span>
                                </div>

                                {/* TENDER & PAYMENT BREAKDOWN */}
                                <div className="pos-payment-breakdown">
                                    <div className="pos-payment-title">Payment Details</div>
                                    <div className="pos-sum-row">
                                        <span className="pos-sum-label">Cash</span>
                                        <span className="pos-sum-val">{formatCurrency(cashPaid)}</span>
                                    </div>

                                    <div className="pos-sum-row">
                                        <span className="pos-sum-label">Card</span>
                                        <span className="pos-sum-val">{formatCurrency(cardPaid)}</span>
                                    </div>
                                </div>

                                {/* DYNAMIC SETTLEMENT STATUS */}
                                {remainingAmount > 0 ? (
                                    <div className="pos-remaining-box">
                                        <div className="pos-rem-left">
                                            <div className="pos-rem-label">Balance Due</div>
                                            <div className="pos-rem-sub">Awaiting payment</div>
                                        </div>
                                        <div className="pos-rem-val">{formatCurrency(remainingAmount)}</div>
                                    </div>
                                ) : isPaidInFull ? (
                                    <div className="pos-paid-box">
                                        <div className="pos-paid-badge">Payment Completed</div>
                                        <div className="pos-paid-sub">Paid in full • Thank you!</div>
                                    </div>
                                ) : null}
                            </div>
                        </Card>
                    </Col>

                    {/* BOTTOM FOOTER COLUMN */}
                    <Col className="cfd-col-footer">
                        <div className="pos-thankyou-footer">
                            <div className="pos-footer-brand">Magna Cash & Carry</div>
                            <div className="pos-footer-msg">Thank you for your business. Please verify items before leaving.</div>
                        </div>
                    </Col></Row>
            </main>
        </div>
    );
};

export default OrderSummery;



